/* eslint-disable react/prop-types */
import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LinkButton, TextFieldWithActions, RadioButton, LabelFormControl } from '@cambridgeassessment/cambridge-ui';
import {
  StyledActionsAndFileTypesContainer,
  StyledComponentContainer,
  StyledRemoveContainerLeft,
  StyledTextFieldContainer,
  useStyles,
} from './styles';
import { ComponentSections, ComponentSectionDoc } from '../../../../service/components/types';
import ComponentSectionFileTypesFC from './ComponentSupportingMaterial';
import { componentFileTypesOnly } from './FileTypeComponent/fileTypes';
import { RootState } from '../../../../store';
import { RadioGroup } from '@material-ui/core';
import thunks from '../../../../store/components/thunks';
import { Authorized } from 'lib-browser';
import { ComponentDocumentsMultipleFilesSwitch } from './ComponentDocumentsMultipleFilesSwitch/index';
import { Roles } from '@cambridgeassessment/lib-ee-commons';

const HELPER_TEXT = 'Change the name for the document section';

interface Props {
  componentSection: ComponentSections | ComponentSectionDoc;
  componentId: string;
  index: number;
  submit: (componentId: string, section: ComponentSections | ComponentSectionDoc) => void;
  remove: (componentId: string, id: number) => void;
  removeEnabled: boolean;
  checkName: (name: string, index: number) => boolean;
  disabledOptions?: boolean;
  deleteLocalSection: () => void;
  disabled?: boolean;
  isLocalComponentSection?: boolean;
}

const ComponentSectionFC: React.FC<Props> = ({
  componentSection,
  submit,
  componentId,
  remove,
  index,
  removeEnabled,
  checkName,
  disabledOptions,
  deleteLocalSection,
  disabled,
  isLocalComponentSection,
}): React.ReactElement => {
  const dispatch = useDispatch();
  const inputEl = useRef<HTMLInputElement>(document.createElement('input'));
  const [inputValue, setInputValue] = useState<string>(componentSection.name);
  const [radioValue, setRadioValue] = useState<boolean>(componentSection.isMandatory);
  const userSelectedRole = useSelector((state: RootState) => state.userData.selectedRole);
  const isUserComplianceOfficer = userSelectedRole?.name === Roles.SUBMIT_COMPLIANCE;
  const isUserAssessmeentManager = userSelectedRole?.name === Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER;
  const isUserAdministrator = userSelectedRole?.name === Roles.SUBMIT_ADMINISTRATOR;

  const [errorMsg, setErrorMsg] = useState<string>('');

  const onSubmit = (): void => {
    if (!inputValue) {
      setErrorMsg('Name must have at least 1 character');
    } else if (inputValue.length > 150) {
      setErrorMsg('Name cannot exceed 150 characters');
    } else if (!checkName(inputValue, index)) {
      inputEl.current.blur();
      const updatedSection = {
        ...componentSection,
        name: inputValue,
        fileTypes: componentFileTypesOnly,
      };
      submit(componentId, updatedSection);
    } else {
      setErrorMsg('Name already exists');
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const radioBoolValue = (event.target as HTMLInputElement).value == 'true';
    setRadioValue(radioBoolValue);
    const updatedSection = {
      ...componentSection,
      isMandatory: radioBoolValue,
    } as ComponentSections;
    dispatch(thunks.updateComponentSection(componentId, updatedSection, 'isMandatory', updatedSection.id));
  };

  const onChange = (value: string): void => {
    if (errorMsg) {
      setErrorMsg('');
    }
    if (componentSection.name && !value) {
      setErrorMsg('Name must have at least  1 character');
    }
    if (value.length > 150) {
      setErrorMsg('Name cannot exceed 150 characters');
    }
    setInputValue(value);
  };

  const setDefaultOrEmpty = (): void => {
    setInputValue(componentSection.name);
    setErrorMsg('');
  };

  const onCancel = (): void => {
    setDefaultOrEmpty();
    deleteLocalSection();
  };

  const removeItem = (): void => {
    if (!isUserComplianceOfficer && !isUserAssessmeentManager) {
      remove(componentId, (componentSection as ComponentSections).id);
    }
  };

  const classes = useStyles();

  return (
    <StyledActionsAndFileTypesContainer>
      <StyledComponentContainer>
        <StyledTextFieldContainer>
          <Authorized
            roles={[Roles.SUBMIT_SUPER_ADMIN]}
            readOnlyRoles={[
              Roles.SUBMIT_COMPLIANCE,
              Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
              Roles.SUBMIT_ADMINISTRATOR,
            ]}
            selectedRole={userSelectedRole}
          >
            <TextFieldWithActions
              disabled={isUserComplianceOfficer || isUserAssessmeentManager || isUserAdministrator || disabled}
              dataQaId={`document-name-input-${componentSection.name}`}
              helperText={isLocalComponentSection ? undefined : HELPER_TEXT}
              onCancelEditing={onCancel}
              onChange={onChange}
              onSaveField={onSubmit}
              errorMsg={errorMsg}
              value={inputValue}
              label='Document name'
              placeholder='Enter a name for the document'
            />
          </Authorized>
        </StyledTextFieldContainer>
        <RadioGroup aria-label='is document mandatory' value={radioValue} onChange={handleRadioChange}>
          <Authorized
            roles={[Roles.SUBMIT_SUPER_ADMIN]}
            readOnlyRoles={[
              Roles.SUBMIT_COMPLIANCE,
              Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
              Roles.SUBMIT_ADMINISTRATOR,
            ]}
            selectedRole={userSelectedRole}
          >
            <LabelFormControl
              disabled={disabledOptions || disabled}
              value={true}
              control={<RadioButton dataQaId={`${componentSection.name}-mandatory`} />}
              label={'Mandatory'}
              dataQaId={`${componentSection.name}-mandatory-label-control`}
            />
            <LabelFormControl
              disabled={disabledOptions || disabled}
              value={false}
              control={<RadioButton dataQaId={`${componentSection.name}-optional`} />}
              label={'Optional'}
              dataQaId={`${componentSection.name}-optional-label-control`}
            />
          </Authorized>
        </RadioGroup>
      </StyledComponentContainer>
      <ComponentDocumentsMultipleFilesSwitch
        componentId={componentId}
        componentSection={componentSection}
        checked={!!componentSection.allowMultipleFiles}
        disabled={disabledOptions || disabled}
      />
      <ComponentSectionFileTypesFC
        componentSection={componentSection}
        disabled={disabledOptions || disabled}
        componentId={componentId}
      >
        {removeEnabled && !isUserComplianceOfficer && !isUserAssessmeentManager && !isUserAdministrator && (
          <StyledRemoveContainerLeft>
            <LinkButton
              arrowType='up'
              endIcon={false}
              onClick={removeItem}
              dataQaId={`document-remove-${componentSection.name}`}
              passedClassName={classes.root}
              disabled={disabled}
              ariaLabel={'Remove'}
            >
              Remove
            </LinkButton>
          </StyledRemoveContainerLeft>
        )}
      </ComponentSectionFileTypesFC>
    </StyledActionsAndFileTypesContainer>
  );
};

export default ComponentSectionFC;
